import * as z from "zod";
import { userFacingError } from "../authorization";
import { zql } from "../generatedDrizzleSchema";
import { hasAccess } from "../rbac/access";
import {
  isTenantScopeGrant,
  type Permission,
  SHAREABLE_SCOPE_KINDS,
} from "../rbac/permissions";
import { assertBuiltinRoleScope } from "../rbac/validation/grantShape";
import { BUILTIN_ROLES, type BuiltinRoleId } from "../rbac/roles";
import { generateId } from "./utils";
import { mutator } from "./mutator";

// One grant the service account holds: a role on a target scope. Unlike
// setScopeRoleGrants (fixed scope, varying principal), here the principal is
// fixed (the service account) and each entry names a different scope.
const accountGrantEntry = z.object({
  scopeId: z.string(),
  scopeKind: z.enum(SHAREABLE_SCOPE_KINDS),
  builtinRoleId: z.string().optional(),
  customRoleId: z.string().optional(),
});

export const setServiceAccountGrantsSchema = z.object({
  serviceAccountId: z.string(),
  grants: z.array(accountGrantEntry),
});

function entryKey(g: {
  scopeId: string;
  builtinRoleId?: string | null;
  customRoleId?: string | null;
}): string {
  return `${g.scopeId}:${g.builtinRoleId ?? ""}:${g.customRoleId ?? ""}`;
}

// Sets the grants a service account holds (what it is allowed to do). Gated on
// service_account:manage for the account, PLUS a no-escalation guard: the
// granting user may only confer a permission they already hold on that scope.
// Without this, anyone who can manage an account could grant it -- and thus
// assume -- access they lack themselves. `hasAccess` lets a tenant admin
// (allow_all) through automatically.
export const setServiceAccountGrants = mutator({
  schema: setServiceAccountGrantsSchema,
  resolve: {
    serviceAccount: { kind: "serviceAccount", from: "serviceAccountId" },
  },
  permissions: [
    {
      permission: "service_account:manage",
      scopeId: ({ args }) => args.serviceAccount.id,
    },
  ],
  fn: async ({ tx, ctx, args: { serviceAccount, grants } }) => {
    for (const grant of grants) {
      if (!!grant.builtinRoleId === !!grant.customRoleId) {
        throw userFacingError(
          "Each grant must specify exactly one of builtinRoleId or customRoleId"
        );
      }
      if (grant.builtinRoleId) {
        assertBuiltinRoleScope(grant.builtinRoleId, grant.scopeKind);
      }

      // A personal space belongs to a single user; a service account identity
      // has no business holding access to it, so reject such grants outright.
      if (grant.scopeKind === "space") {
        const space = await tx.run(
          zql.spaces
            .where("id", grant.scopeId)
            .where("tenantId", ctx.tenantId)
            .one()
        );
        if (space?.mode === "personal") {
          throw userFacingError(
            "A service account cannot be granted access to a personal space"
          );
        }
      }

      // Resolve the permissions this grant would confer on the account.
      let conferred: readonly Permission[];
      if (grant.builtinRoleId) {
        conferred =
          BUILTIN_ROLES[grant.builtinRoleId as BuiltinRoleId]?.permissions ??
          [];
      } else {
        // Scope the role to the tenant: customRoleId is caller-supplied and the
        // lookup is unscoped (no RLS), so a foreign role must read as not found.
        const customRole = await tx.run(
          zql.roles
            .where("id", grant.customRoleId!)
            .where("tenantId", ctx.tenantId)
            .related("rolePermissions")
            .one()
        );
        if (!customRole) throw userFacingError("Custom role not found");
        conferred = (customRole.rolePermissions ?? []).map(
          (r) => r.permission as Permission
        );
      }
      if (conferred.includes("allow_all")) {
        throw userFacingError(
          "Cannot grant a service account a role carrying allow_all"
        );
      }

      // No-escalation guard: the granter must already hold each conferred
      // permission on the target scope (tenant admins pass via allow_all).
      // Checks the acting principal (`ctx.principal`, e.g. a service account)
      // — a service account granter's userId is its own service-account id, so
      // without this it would be validated as a bogus user and hold nothing.
      for (const permission of conferred) {
        const granterHas = await hasAccess({
          tx,
          userId: ctx.userId,
          principal: ctx.principal,
          scopeId: grant.scopeId,
          tenantId: ctx.tenantId,
          permission,
        });
        if (!granterHas) {
          throw userFacingError(
            `You cannot grant "${permission}" because you do not hold it on this scope`
          );
        }
      }
    }

    // Replace the account's resource-scoped grant set. Fetch existing grants
    // held by the account (as principal), diff by (scope, role), delete
    // removed, insert new. Tenant roles are excluded: they're managed
    // separately by setServiceAccountTenantRoles and would otherwise be wiped
    // out here since `grants` never carries a tenant-scoped entry.
    const existing = (
      await tx.run(zql.roleGrants.where("serviceAccountId", serviceAccount.id))
    ).filter((g) => !isTenantScopeGrant(g));
    const desiredKeys = new Set(grants.map(entryKey));
    const existingKeys = new Set(existing.map(entryKey));

    for (const row of existing) {
      if (!desiredKeys.has(entryKey(row))) {
        await tx.mutate.roleGrants.delete({ id: row.id });
      }
    }

    const now = Date.now();
    for (const grant of grants) {
      if (existingKeys.has(entryKey(grant))) continue;
      await tx.mutate.roleGrants.insert({
        id: generateId(),
        serviceAccountId: serviceAccount.id,
        builtinRoleId: (grant.builtinRoleId as BuiltinRoleId) ?? undefined,
        customRoleId: grant.customRoleId,
        scopeId: grant.scopeId,
        createdAt: now,
        tenantId: ctx.tenantId,
      });
    }
  },
});
